import { useLocale } from "../../../../hooks/useLocale";
import { msg } from "../../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime, parseServerDate } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { ReactNode, useEffect, useState } from "react";
import styled from "styled-components";
import { GD } from "src/GD";
import { cents } from "src/tools/tools";
import { getNotificationErrorMessage } from "src/tools/notify";
import type { IPaymentSession } from "src/types/types";
import { Pagination } from "src/view/conponents/Pagination";
import { ChargebackDetailsPopup, chargebackStatusLabel, CopyableValue, Shimmer, statusTone } from "src/view/chargebacks/Chargebacks";
import { AlertCircle, ChevronRight, RefreshCw, Undo2 } from "lucide-react";
import { ChargebackAnalytics } from "src/view/chargebacks/ChargebackAnalytics";

interface IClientChargebacksProps {
	clientID?: number;
	merchantID?: number;
	merchantName?: string;
}

interface IClientChargeback {
	id: number;
	session_id: number;
	payment_id?: string | number | null;
	status: string;
	amount: number;
	currency: string;
	merchant_id: number;
	client_id: number;
	created_at: string | number;
}

interface IChargebacksPagination {
	total: number;
	page: number;
	perPage: number;
	totalPages: number;
}

interface IChargebacksResponse {
	items: IClientChargeback[];
	pagination: IChargebacksPagination;
}

interface IClientMerchantGroup {
	id: number;
	name?: string;
	merchants?: Array<{ id: number; name?: string }>;
}

const PER_PAGE = 20;

const formatRowDate = (value: string | number) => {
	const date = parseServerDate(value);
	if (!date || Number.isNaN(date.getTime())) return value ? String(value) : "—";
	return formatDisplayDateTime(date, { date: { day: "2-digit", month: "2-digit", year: "numeric" }, time: { hour: "2-digit", minute: "2-digit" }, separator: " " });
};

// Same ledger look as the statements list: one white card, a row per chargeback, the amount on the right.
const ChargebackList = styled.div`
	--columns: minmax(150px, 1.1fr) 120px minmax(160px, 1.2fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(120px, .8fr) 20px;
	container-type: inline-size;
	min-width: 0;
	overflow: hidden;
	border: 1px solid #d0d5dd;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	color: #344054;
	.list-head, .list-row { display: grid; grid-template-columns: var(--columns); align-items: center; gap: 16px; padding: 0 20px; }
	.list-head { min-height: 40px; border-bottom: 1px solid #eaecf0; background: #f9fafb; }
	.list-head > * { color: #667085; font-size: 11px; font-weight: 600; letter-spacing: .04em; line-height: 16px; text-transform: uppercase; white-space: nowrap; }
	.right { justify-self: end; text-align: right; }
	.list-row + .list-row { border-top: 1px solid #eaecf0; }
	.list-row { min-height: 56px; padding-block: 10px; cursor: pointer; }
	.list-row:hover { background: #f8fafc; }
	.list-row > * { min-width: 0; }
	.primary { display: block; overflow: hidden; color: #182230; font-size: 14px; font-weight: 500; font-variant-numeric: tabular-nums; text-overflow: ellipsis; white-space: nowrap; }
	.secondary { display: block; color: #98a2b3; font-size: 12px; font-variant-numeric: tabular-nums; }
	.name { display: block; overflow: hidden; color: #182230; text-overflow: ellipsis; white-space: nowrap; }
	.mono { display: block; color: #667085; font-variant-numeric: tabular-nums; }
	.amount { justify-self: end; color: #182230; font-size: 15px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.amount small { margin-left: 4px; color: #98a2b3; font-size: 11px; font-weight: 400; }
	.chev { display: flex; color: #98a2b3; }
	.list-row:hover .chev { color: #175cd3; }
	.message { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 48px 24px; color: #98a2b3; text-align: center; }
	.message strong { margin-top: 6px; color: #182230; font-size: 14px; font-weight: 600; }
	.message span { max-width: 360px; color: #667085; }
	.cell-label { display: none; }
	/* Narrow: date and amount on top, the rest underneath; ids get their labels back since the header is hidden. */
	@container (max-width: 900px) {
		.list-head { display: none; }
		.list-row { grid-template-columns: minmax(0, 1fr) auto; gap: 6px 16px; padding-block: 12px; }
		.list-row > .amount { grid-row: 1; grid-column: 2; }
		.list-row > .chev { display: none; }
		.list-row > :not(.when):not(.amount) { grid-column: 1 / -1; }
		.cell-label { display: inline; margin-right: 4px; color: #98a2b3; }
		.id-cell { display: flex; align-items: center; }
	}
`;

const StatusBadge = ({ status }: { status?: string }) => {
	useLocale();
	if (!status) return <span className="secondary">—</span>;
	const tone = statusTone(status);
	return (
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: "6px",
				width: "max-content",
				padding: "2px 8px",
				borderRadius: "6px",
				border: `1px solid ${tone.border}`,
				backgroundColor: tone.background,
				color: tone.color,
				fontSize: "11px",
				lineHeight: "16px",
				fontWeight: 600,
				whiteSpace: "nowrap",
			}}>
			<Box sx={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "currentColor" }} />
			{chargebackStatusLabel(status)}
		</Box>
	);
};

const ChargebackRow = ({ row, merchantName, onOpen }: { row: IClientChargeback; merchantName?: string; onOpen: () => void }) => {
	useLocale();
	const hasPaymentId = row.payment_id != null && row.payment_id !== "";
	return (
		<div className="list-row" role="row" onClick={onOpen}>
			<span className="when" role="cell">
				<span className="primary">
					<DateTimeTooltip value={row.created_at}>{formatRowDate(row.created_at)}</DateTimeTooltip>
				</span>
				<span className="secondary">
					<CopyableValue value={row.id} />
				</span>
			</span>
			<span role="cell">
				<StatusBadge status={row.status} />
			</span>
			<span role="cell">
				<span className="name">{merchantName || msg("merchant")}</span>
				<span className="secondary">
					<CopyableValue value={row.merchant_id} />
				</span>
			</span>
			<span className="id-cell" role="cell">
				<span className="cell-label">{msg("sessionId")}:</span>
				<span className="mono">
					<CopyableValue value={row.session_id} />
				</span>
			</span>
			<span className="id-cell" role="cell">
				<span className="cell-label">{msg("paymentId_d9c9afd8")}:</span>
				{hasPaymentId ? (
					<span className="mono">
						<CopyableValue value={row.payment_id as string | number} />
					</span>
				) : (
					<span className="secondary">—</span>
				)}
			</span>
			<span className="amount" role="cell">
				{cents(row.amount)}
				<small>{row.currency || ""}</small>
			</span>
			<span className="chev" aria-hidden="true">
				<ChevronRight size={16} />
			</span>
		</div>
	);
};

const SkeletonRow = ({ index }: { index: number }) => (
	<div className="list-row" style={{ cursor: "default" }}>
		<VBox gap="7px">
			<Shimmer $width="70%" $height="13px" />
			<Shimmer $width="45%" $height="9px" />
		</VBox>
		<Shimmer $width="76px" $height="20px" $radius="6px" />
		<VBox gap="7px">
			<Shimmer $width={`${55 + (index % 4) * 10}%`} $height="12px" />
			<Shimmer $width="40%" $height="9px" />
		</VBox>
		<Shimmer $width="80%" $height="12px" />
		<Shimmer $width={`${60 + (index % 2) * 20}%`} $height="12px" />
		<HBox sx={{ justifyContent: "flex-end" }}>
			<Shimmer $width={`${50 + (index % 3) * 12}%`} $height="14px" />
		</HBox>
		<span />
	</div>
);

const ListMessage = ({ icon, title, text }: { icon: ReactNode; title: string; text?: string }) => (
	<div className="message">
		{icon}
		<strong>{title}</strong>
		{text && <span>{text}</span>}
	</div>
);

type TChargebackRecordState = { data?: Record<string, unknown>; error?: string; busy: boolean };

const asRecord = (value: unknown) => (value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined);
const pickString = (record: Record<string, unknown> | undefined, key: string) => (typeof record?.[key] === "string" && record[key] !== "" ? (record[key] as string) : undefined);
const pickNumber = (record: Record<string, unknown> | undefined, key: string) => (typeof record?.[key] === "number" ? (record[key] as number) : undefined);

/**
 * Opens the chargeback itself: the list row is shown at once, the full record from chargebacks/get fills in when it
 * arrives. raw.chargeback is the same Decta record the processing popup reads from chargeback files, so its dispute,
 * transaction and merchant cards are reused as is; raw.session adds the payment as processing sees it.
 */
const ClientChargebackPopup = ({ row, merchantName, onClose }: { row: IClientChargeback; merchantName?: string; onClose: () => void }) => {
	useLocale();
	const [record, setRecord] = useState<TChargebackRecordState>({ busy: true });

	useEffect(() => {
		let active = true;
		setRecord({ busy: true });
		const load = async () => {
			try {
				const response = await GD.REQ_API.request({ method: "chargebacks/get", data: { id: row.id } });
				if (!active) return;
				if (response.error) {
					setRecord({ busy: false, error: response.error.message || msg("cbLoadFailed") });
					return;
				}
				setRecord({ busy: false, data: asRecord(response.data) ?? {} });
			} catch (error) {
				if (active) setRecord({ busy: false, error: getNotificationErrorMessage(error, msg("cbLoadFailed")) });
			}
		};
		load();
		return () => {
			active = false;
		};
	}, [row.id]);

	const data = record.data;
	const raw = asRecord(data?.raw);
	const disputeRecord = asRecord(raw?.chargeback);
	const session = asRecord(raw?.session);
	const dectaPaymentId = pickString(disputeRecord, "PAYMENT_ID");

	return (
		<ChargebackDetailsPopup
			chargeback={{
				id: row.id,
				// The loaded record wins over the list row for the values it carries.
				amount: pickNumber(data, "amount") ?? pickNumber(raw, "chargeback_amount") ?? row.amount,
				currency: pickString(data, "currency") ?? pickString(raw, "chargeback_currency") ?? row.currency,
				status: pickString(data, "status") ?? pickString(raw, "chargeback_status") ?? row.status,
				created_at: pickString(data, "created_at") ?? row.created_at,
				chargeback_date: pickString(raw, "chargeback_date"),
				session_id: row.session_id,
				payment_id: pickString(data, "payment_id") ?? row.payment_id ?? undefined,
				decta_payment_id: dectaPaymentId,
				merchant_id: row.merchant_id,
			}}
			merchantName={merchantName}
			showFileDetails={record.busy || !!disputeRecord}
			fileData={disputeRecord}
			fileBusy={record.busy}
			session={session}
			recordError={record.error}
			raw={data}
			showResend={false}
			onOpenTransaction={() => GD.S_TRANSACTION_PREVIEW.invoke({ id: row.session_id } as IPaymentSession)}
			onClose={onClose}
		/>
	);
};

export const ClientChargebacks = ({ clientID, merchantID, merchantName }: IClientChargebacksProps) => {
	useLocale();
	const [currentPage, setCurrentPage] = useState(0);
	const [openedChargeback, setOpenedChargeback] = useState<IClientChargeback | undefined>(undefined);
	const clientFilter = clientID != null ? { client_id: clientID } : {};
	const getRequest = (page: number) => ({
		method: "chargebacks/list",
		data: {
			...(merchantID ? { merchant_id: merchantID } : clientFilter),
			page,
			limit: PER_PAGE,
		},
	});
	const [response, reload, _reset, busy] = GD.REQ_API.useRequest(null, getRequest(currentPage));
	const [merchantsResponse, reloadMerchants, _resetMerchants, merchantsBusy] = GD.REQ_API.useRequest(
		undefined,
		merchantID ? undefined : { method: "merchants/list", data: clientFilter },
	);

	const data = response?.data as IChargebacksResponse | undefined;
	const items = Array.isArray(data?.items) ? data.items : [];
	const pagination = data?.pagination;
	const loading = busy || !response;
	const noData = !loading && !response?.error && items.length === 0;
	const merchantGroups: IClientMerchantGroup[] = Array.isArray(merchantsResponse?.data) ? merchantsResponse.data : [];
	const merchantNames = new Map<number, string>();
	if (merchantID && merchantName) merchantNames.set(merchantID, merchantName);
	merchantGroups.forEach(group => {
		if (group.name) merchantNames.set(Number(group.id), group.name);
		group.merchants?.forEach(merchant => {
			if (merchant.name && !merchantNames.has(Number(merchant.id))) merchantNames.set(Number(merchant.id), merchant.name);
		});
	});

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		reload(getRequest(page));
	};

	const refresh = () => {
		reload(getRequest(currentPage));
		if (!merchantID) reloadMerchants({ method: "merchants/list", data: clientFilter });
	};
	const merchantLookupBusy = !merchantID && merchantsBusy;
	// Keep the page height steady while a page loads: as many skeleton rows as the page it replaces.
	const skeletonRows = Math.max(items.length, 6);

	return (
		<VBox gap="16px" sx={{ flex: 1, minWidth: 0, padding: "20px 0" }}>
			{/* chargebacks/analytics has no merchant_id, so merchant pages show the list only. */}
			{!merchantID && <ChargebackAnalytics clientID={clientID} />}
			<ChargebackList role="table" aria-label={msg("chargebacks_655ad8a5")}>
				<div className="list-head" role="row">
					<span role="columnheader">{msg("cbListChargeback")}</span>
					<span role="columnheader">{msg("status")}</span>
					<span role="columnheader">{msg("merchant")}</span>
					<span role="columnheader">{msg("sessionId")}</span>
					<span role="columnheader">{msg("paymentId_d9c9afd8")}</span>
					<span role="columnheader" className="right">{msg("amount")}</span>
					<span />
				</div>

				{loading ? (
					Array.from({ length: skeletonRows }, (_, index) => <SkeletonRow key={index} index={index} />)
				) : response?.error ? (
					<ListMessage icon={<AlertCircle size={24} color="#d92d20" />} title={msg("errorLoadingChargebacks")} text={response.error.message} />
				) : noData ? (
					<ListMessage
						icon={<Undo2 size={24} />}
						title={msg("noChargebacksFound")}
						text={merchantID ? msg("thereAreNoChargebacksForThisMerchant") : clientID != null ? msg("thereAreNoChargebacksForThisClient") : msg("thereAreNoChargebacks")}
					/>
				) : (
					items.map(row => (
						<ChargebackRow key={row.id} row={row} merchantName={merchantNames.get(Number(row.merchant_id))} onOpen={() => setOpenedChargeback(row)} />
					))
				)}
			</ChargebackList>

			<HBox sx={{ gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
				<Pagination pagination={pagination} onPageChange={handlePageChange} disabled={busy} />
				<Spacer grow />
				<Button title={msg("refresh")} variant="secondarySmall" icon={<RefreshCw size={15} />} disabled={busy || merchantLookupBusy} onClick={refresh} />
			</HBox>

			{openedChargeback && (
				<ClientChargebackPopup
					row={openedChargeback}
					merchantName={merchantNames.get(Number(openedChargeback.merchant_id))}
					onClose={() => setOpenedChargeback(undefined)}
				/>
			)}
		</VBox>
	);
};
